/**
 * src/utils/formatDate.js
 *
 * Le format de date de l'application, defini UNE fois.
 *
 * Il n'existait aucun helper central : une vingtaine ont ete reinventes page
 * par page, et six d'entre eux avaient perdu la langue en chemin — d'ou des
 * « 9/2/2026 » et des « September 2, 2026 » dans une interface francaise.
 *
 * La sortie reprend exactement celle des appels deja corrects :
 *   fr -> « 2 sept. 2026 »        en -> « Sep 2, 2026 »
 * Ce n'est pas de l'ISO : c'est le format que l'application produit deja
 * partout ailleurs, et on repare les exceptions, pas la regle.
 *
 * 🔴 LE PIEGE DE LA DATE NUE
 * `new Date('2026-09-02')` est lu en UTC puis rendu en heure locale : au
 * Quebec (UTC-4/-5) cela affiche le 1er septembre. Plusieurs helpers locaux
 * contournaient deja le probleme avec 'T12:00:00' ; midi est assez loin des
 * deux bords pour qu'aucun fuseau ne fasse changer le jour. Cette precaution
 * est reprise ici — sans elle, TOUTE date nue reculerait d'un jour.
 * Un horodatage complet, lui, porte deja son fuseau : on n'y touche pas.
 */

// Accepte 'fr' comme 'fr-CA' : certains appelants portent une langue, d'autres
// une locale complete.
const localeOf = (language) =>
  String(language || '')?.toLowerCase()?.startsWith('fr') ? 'fr-CA' : 'en-CA';

/** Date nue (YYYY-MM-DD) -> midi local. Horodatage complet -> inchange. */
const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/?.test(s) ? new Date(`${s}T12:00:00`) : new Date(s);
  return Number.isNaN(d?.getTime()) ? null : d;
};

/** « 2 sept. 2026 » / « Sep 2, 2026 ». Rend un tiret cadratin si illisible. */
export const formatDate = (value, language = 'fr') => {
  const d = toDate(value);
  if (!d) return '—';
  return d?.toLocaleDateString(localeOf(language), {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

/** « 2 septembre 2026 » / « September 2, 2026 ». Pour les pieds de document. */
export const formatDateLong = (value, language = 'fr') => {
  const d = toDate(value);
  if (!d) return '—';
  return d?.toLocaleDateString(localeOf(language), {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

/**
 * « jeudi 10 septembre 2026 » / « Thursday, September 10, 2026 ».
 *
 * Meme regle que formatDateLong, avec le jour de la semaine devant. En francais
 * ni le jour ni le mois ne prennent de majuscule : si l'appelant en veut une en
 * tete de phrase, il applique `first-letter:uppercase` — surtout pas la classe
 * CSS `capitalize`, qui met une majuscule a CHAQUE mot et produirait
 * « Jeudi 10 Septembre 2026 ».
 */
export const formatDateWeekday = (value, language = 'fr') => {
  const d = toDate(value);
  if (!d) return '—';
  return d?.toLocaleDateString(localeOf(language), {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
};

export default formatDate;
